import { useQuery, useSuspenseQuery } from "@apollo/client/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTypedParams, useTypedSearchParams } from "react-router-typesafe-routes";
import { DetailLoading } from "~/components/loading";
import { Button, Input } from "~/components/ui";
import {
  GetReferenceFileDetailDocument,
  type GetReferenceFileDetailQuery,
  type GetReferenceFileDetailQueryVariables,
  GetReferenceFileImagePresignedUrlDocument,
  type GetReferenceFileImagePresignedUrlQuery,
  type GetReferenceFileImagePresignedUrlQueryVariables,
} from "~/features/references/gqls";
import { useTenant } from "~/features/tenant/context";
import { v2Route } from "~/v2/routes";

/** Standalone popup window: paginated page-image viewer for a reference file. */
export function ReferenceFileViewerPage() {
  const { tenantId } = useTenant();
  const { referenceId = "", referenceFileId = "" } = useTypedParams(v2Route.referenceFiles.slides);
  const [{ page: pageParam, folio: folioParam }, setSearchParams] = useTypedSearchParams(v2Route.referenceFiles.slides);
  if (!tenantId) throw new Error("Tenant ID is required");

  const { data } = useSuspenseQuery<GetReferenceFileDetailQuery, GetReferenceFileDetailQueryVariables>(
    GetReferenceFileDetailDocument,
    { variables: { tenantId, id: referenceFileId } },
  );
  const file = data.referenceFiles[0];
  const folioOffset = file?.folioOffset ?? 0;
  const numPages = file?.numPages ?? null;

  // Resolve the initial page: explicit `page`, else `folio + folioOffset`, else 1 (V1 parity).
  // Clamp to the page count up front — a citation folio can point past the last page, and an
  // out-of-range page makes the image query fail server-side.
  const initialPage = pageParam ?? (folioParam != null ? folioParam + folioOffset : 1);
  const [page, setPage] = useState(numPages ? Math.min(Math.max(1, initialPage), numPages) : Math.max(1, initialPage));

  const goto = (next: number) => {
    const clamped = Math.max(1, numPages ? Math.min(numPages, next) : next);
    setPage(clamped);
    setSearchParams((prev) => ({ ...prev, page: clamped }), { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-bg-page">
      <div className="flex-1 overflow-auto p-2">
        <PageImage tenantId={tenantId} referenceId={referenceId} referenceFileId={referenceFileId} page={page} />
      </div>
      <div className="sticky bottom-0 flex items-center justify-center gap-3 border-t border-border-default bg-bg-surface p-2">
        <Button type="button" variant="outline" size="icon" onClick={() => goto(page - 1)} disabled={page <= 1}>
          <ChevronLeft />
        </Button>
        <Input
          type="number"
          inputMode="numeric"
          value={page}
          onChange={(e) => goto(Number(e.target.value) || 1)}
          className="w-20 text-center tabular-nums"
          aria-label="page"
        />
        {numPages != null && <span className="text-12-regular tabular-nums text-fg-3">/ {numPages}</span>}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => goto(page + 1)}
          disabled={numPages != null && page >= numPages}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

function PageImage({
  tenantId,
  referenceId,
  referenceFileId,
  page,
}: {
  tenantId: string;
  referenceId: string;
  referenceFileId: string;
  page: number;
}) {
  const { t } = useTranslation();
  // Non-suspense + error-tolerant: a missing/failed page image must not crash the viewer.
  const { data, loading, error } = useQuery<
    GetReferenceFileImagePresignedUrlQuery,
    GetReferenceFileImagePresignedUrlQueryVariables
  >(GetReferenceFileImagePresignedUrlDocument, { variables: { tenantId, referenceId, referenceFileId, page } });

  if (loading) return <DetailLoading />;
  const url = data?.cnutGetReferenceFileImagePresignedUrl?.url;
  if (error || !url) {
    return <div className="py-10 text-center text-[13px] text-fg-3">{t("feature.common.noData")}</div>;
  }
  return <img src={url} alt={`page ${page}`} className="mx-auto w-full max-w-3xl" />;
}
